/* RESPONSIBLE TEAM: team-proactive-support */
/* eslint-disable ember/no-assignment-of-untracked-properties-used-in-tracking-contexts */
import { isPresent } from '@ember/utils';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Node from './node';
import type Edge from './edge';
import Offset from './offset';
import Coordinates from './coordinates';
import Bounds from './bounds';
import type Graph from './graph';
import { A } from '@ember/array';
import type MutableArray from '@ember/array/mutable';
import { schedule, debounce } from '@ember/runloop';

enum Direction {
  Vertical,
  Horizontal,
}

export default class State {
  @tracked graph: Graph;

  @tracked wrapperBoundingRect?: DOMRect;

  @tracked draggingNode?: Node;
  @tracked draggingEdge?: Edge;
  @tracked selectedEdge?: Edge;
  @tracked mouseAtEdge?: Edge;
  @tracked selectedNodes: MutableArray<Node> = A();
  @tracked cursorOffset: Offset = new Offset(0, 0);
  @tracked positioningCoordinates: Coordinates = new Coordinates(0, 0);
  @tracked selectionStartCoordinates?: Coordinates;
  @tracked mouseCoordinates: Coordinates = new Coordinates(0, 0);
  @tracked screenCoordinates?: Coordinates;

  @tracked scale = 1;
  @tracked gridOffset = new Offset(0, 0);
  @tracked scaleTranslate = new Offset(0, 0);
  @tracked transformOrigin = new Coordinates(0, 0);

  @tracked inPanningMode = false;
  @tracked disableScroll = false;
  @tracked lastPanningCoordinates?: Coordinates;

  @tracked nodesHaveBeenRendered = false;

  get isPanning() {
    return isPresent(this.lastPanningCoordinates);
  }

  constructor(graph: Graph) {
    this.graph = graph;
  }

  @action updateWrapperBoundingRect(element: HTMLElement) {
    this.wrapperBoundingRect = element.getBoundingClientRect();
  }

  @action markNodesAsRendered(): void {
    schedule('afterRender', this, () => (this.nodesHaveBeenRendered = true));
  }

  @computed(
    'graph.wrapper',
    'scale',
    'gridOffset.{x,y}',
    'scaleTranslate.{x,y}',
    'wrapperBoundingRect',
  )
  get viewportBounds(): Bounds {
    if (this.wrapperBoundingRect) {
      let rect = this.wrapperBoundingRect;
      let width = rect.width / this.scale;
      let height = rect.height / this.scale;
      let origin = Coordinates.scaled(rect.x, rect.y, this.graph);
      return new Bounds(origin.y, origin.x, width, height);
    } else {
      return new Bounds(0, 0, 0, 0);
    }
  }

  addSelectedNode(node: Node): void {
    if (!this.selectedNodes.includes(node) && this.graph.hooks.canSelectNode(node)) {
      this.selectedNodes.pushObject(node);
      node.edges.forEach((edge) => {
        this.graph.edges.removeObject(edge);
        this.graph.edges.pushObject(edge);
      });
    }
  }

  removeSelectedNode(node: Node): void {
    if (this.selectedNodes.includes(node)) {
      this.selectedNodes.removeObject(node);
    }
  }

  setMouseAtEdge(edge: Edge): void {
    this.graph.edges.removeObject(edge);
    this.graph.edges.pushObject(edge);
    this.mouseAtEdge = edge;
  }

  removeMouseFromEdge() {
    let selectedEdge = this.selectedEdge;
    if (selectedEdge) {
      this.graph.edges.removeObject(selectedEdge);
      this.graph.edges.pushObject(selectedEdge);
    }
    this.mouseAtEdge = undefined;
  }

  setScale(scale: number): void {
    this.scale = Math.min(
      Math.max(scale, this.graph.settings.scale.minumum),
      this.graph.settings.scale.maximum,
    );
    debounce(this, this.updateNodeBounds, 500);
  }

  private updateNodeBounds(): void {
    this.graph.nodes.forEach((node) => node.updateElementBoundingRect(node.element!));
  }

  get hasVisibleNodes(): boolean {
    if (this.draggingNode) {
      return true;
    }
    return this.graph.nodes.any((node) => node.isWithinViewport);
  }

  get hoveredNodes(): Array<Node> {
    return this.graph.nodes.filter((node) => node.isHovered);
  }

  updateTransformOriginForCoordinates(coordinates: Coordinates) {
    let previousTransformOrigin = this.transformOrigin;

    this.transformOrigin = coordinates;

    this.scaleTranslate.x +=
      (this.transformOrigin.x - previousTransformOrigin.x) * this.translationScalingFactor;

    this.scaleTranslate.y +=
      (this.transformOrigin.y - previousTransformOrigin.y) * this.translationScalingFactor;
  }

  get translationScalingFactor(): number {
    if (this.graph.state.scale >= 1) {
      return this.graph.state.scale - 1;
    } else {
      return -(1 - this.graph.state.scale);
    }
  }

  zoomIn(zoomFactor: number, viewPoint?: Coordinates): void {
    viewPoint = viewPoint ?? this.viewportBounds.centerPoint;
    this.updateTransformOriginForCoordinates(viewPoint);
    this.setScale(this.scale + zoomFactor);
  }

  zoomOut(zoomFactor: number, viewPoint?: Coordinates): void {
    viewPoint = viewPoint ?? this.viewportBounds.centerPoint;
    this.updateTransformOriginForCoordinates(viewPoint);
    this.setScale(this.scale - zoomFactor);
  }

  resetZoom(viewPoint?: Coordinates): void {
    viewPoint = viewPoint ?? this.viewportBounds.centerPoint;
    this.updateTransformOriginForCoordinates(viewPoint);
    this.setScale(1);
  }

  @computed('graph.{edges,element}', 'screenCoordinates.{x,y}')
  get hoveredEdges(): Array<Edge> {
    if (this.screenCoordinates && this.graph.edges) {
      let elements = document.elementsFromPoint(this.screenCoordinates.x, this.screenCoordinates.y);
      elements = Array.from(elements).filter((element) => this.graph.element?.contains(element));
      let path = elements.find((element) => element.classList.contains('graph__edge__hover-path'));
      return this.graph.edges.filter((edge) => edge.element === path);
    } else {
      return [];
    }
  }

  @computed('mouseCoordinates.{x,y}', 'selectionStartCoordinates.{x,y}')
  get selectionBounds(): Bounds | undefined {
    if (this.selectionStartCoordinates) {
      let top = Math.min(this.selectionStartCoordinates.y, this.mouseCoordinates.y);
      let left = Math.min(this.selectionStartCoordinates.x, this.mouseCoordinates.x);
      let right = Math.max(this.selectionStartCoordinates.x, this.mouseCoordinates.x);
      let bottom = Math.max(this.selectionStartCoordinates.y, this.mouseCoordinates.y);
      return new Bounds(top, left, right - left, bottom - top);
    } else {
      return undefined;
    }
  }

  get unselectedNodes(): Array<Node> {
    return this.graph.nodes.reject((node) => node.isSelected || node.isDragging);
  }

  get verticalAlignmentBounds(): Bounds | undefined {
    return this.getAlignmentBounds(Direction.Vertical);
  }

  get horizontalAlignmentBounds(): Bounds | undefined {
    return this.getAlignmentBounds(Direction.Horizontal);
  }

  private getAlignmentBounds(direction: Direction): Bounds | undefined {
    if (!this.draggingNode) {
      return undefined;
    }
    let alignedNodes = this.unselectedNodes.filter((node) => {
      if (direction === Direction.Horizontal) {
        return this.draggingNode!.bounds.alignedHorizontallyWith(node.bounds);
      } else {
        return this.draggingNode!.bounds.alignedVerticallyWith(node.bounds);
      }
    });
    if (isPresent(alignedNodes)) {
      return Bounds.fromList(
        alignedNodes.map((node) => node.bounds).concat([this.draggingNode.bounds]),
      );
    } else {
      return undefined;
    }
  }
}
